import { Badge, Tooltip, Group, Text, Stack } from '@mantine/core';
import { IconWifi, IconWifiOff, IconQuestionMark, IconServer } from '@tabler/icons-react';
import type { OnlineStatus, DeviceStatus } from '@/hooks';

interface OnlineStatusBadgeProps {
  status: OnlineStatus;
  deviceStatus?: DeviceStatus;
  showTooltip?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const statusConfig: Record<OnlineStatus, { color: string; label: string; icon: React.ReactNode }> = {
  online: {
    color: 'green',
    label: 'Online',
    icon: <IconWifi size={12} />,
  },
  offline: {
    color: 'red',
    label: 'Offline',
    icon: <IconWifiOff size={12} />,
  },
  unknown: {
    color: 'gray',
    label: 'Unknown',
    icon: <IconQuestionMark size={12} />,
  },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleString();
}

function getCheckMethodExplanation(method: string | null, status: string): string | null {
  if (!method) return null;
  if (method === 'no_target') return 'No IP address or hostname configured';
  if (status === 'unknown') {
    if (method === 'ping') return 'Ping check failed';
    if (method.startsWith('tcp:')) return `TCP port ${method.split(':')[1]} unreachable`;
  }
  return null;
}

function TooltipContent({ deviceStatus }: { deviceStatus: DeviceStatus }) {
  const explanation = getCheckMethodExplanation(deviceStatus.checkMethod, deviceStatus.status);

  return (
    <Stack gap={4}>
      <Group gap="xs">
        <Text size="xs" fw={500}>Status:</Text>
        <Text size="xs">{deviceStatus.statusLabel}</Text>
      </Group>
      {explanation && (
        <Text size="xs" c="yellow" fs="italic">{explanation}</Text>
      )}
      {deviceStatus.latencyMs !== null && (
        <Group gap="xs">
          <Text size="xs" fw={500}>Latency:</Text>
          <Text size="xs">{deviceStatus.latencyMs}ms</Text>
        </Group>
      )}
      {deviceStatus.checkMethod && (
        <Group gap="xs">
          <Text size="xs" fw={500}>Method:</Text>
          <Text size="xs">{deviceStatus.checkMethod}</Text>
        </Group>
      )}
      {deviceStatus.status === 'unknown' && deviceStatus.consecutiveFailures > 0 && (
        <Group gap="xs">
          <Text size="xs" fw={500}>Failed checks:</Text>
          <Text size="xs" c="orange">{deviceStatus.consecutiveFailures}/3</Text>
        </Group>
      )}
      <Group gap="xs">
        <Text size="xs" fw={500}>WinRM:</Text>
        <Text size="xs" c={deviceStatus.winrmAvailable ? 'green' : 'gray'}>
          {deviceStatus.winrmAvailable ? 'Available' : 'Unavailable'}
        </Text>
      </Group>
      <Group gap="xs">
        <Text size="xs" fw={500}>Last Checked:</Text>
        <Text size="xs">{formatDate(deviceStatus.lastCheckedAt)}</Text>
      </Group>
      {deviceStatus.status === 'offline' && deviceStatus.lastOnlineAt && (
        <Group gap="xs">
          <Text size="xs" fw={500}>Last Online:</Text>
          <Text size="xs">{formatDate(deviceStatus.lastOnlineAt)}</Text>
        </Group>
      )}
    </Stack>
  );
}

export function OnlineStatusBadge({
  status,
  deviceStatus,
  showTooltip = true,
  size = 'sm',
}: OnlineStatusBadgeProps) {
  const config = statusConfig[status];

  const badge = (
    <Badge
      color={config.color}
      variant="light"
      size={size}
      leftSection={config.icon}
      rightSection={
        deviceStatus?.winrmAvailable ? (
          <IconServer size={12} style={{ opacity: 0.7 }} />
        ) : undefined
      }
    >
      {config.label}
    </Badge>
  );

  if (showTooltip && deviceStatus) {
    return (
      <Tooltip
        label={<TooltipContent deviceStatus={deviceStatus} />}
        position="right"
        withArrow
        multiline
        w={220}
      >
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

/**
 * Simple dot indicator for compact views
 */
export function OnlineStatusDot({ status, size = 8 }: { status: OnlineStatus; size?: number }) {
  const colors: Record<OnlineStatus, string> = {
    online: 'var(--mantine-color-green-6)',
    offline: 'var(--mantine-color-red-6)',
    unknown: 'var(--mantine-color-gray-5)',
  };

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: colors[status],
        display: 'inline-block',
      }}
    />
  );
}
